/**
 * AccountHeader Component
 * Specialized header for account pages - shows when user is on /account/* routes
 * Provides account-focused navigation without shopping distractions
 */

import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { formatCurrency } from '../../utils/formatters';
import { useStoreCredits } from '../../hooks/useStoreCredits';

const AccountHeader = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    user,
    isAuthenticated,
    logout,
    company: currentCompany,
    store: currentStore,
    canProceedToShopping,
    getStoreDisplayName,
    getUserDisplayName,
    isEmployee
  } = useAuth();

  const isEmp = isEmployee();
  const { enabled: creditsEnabled } = useStoreCredits();
  
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const handleLogout = () => {
    logout();
    setShowMobileMenu(false);
    navigate('/login');
  };

  const toggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu);
  };

  const closeMobileMenu = () => {
    setShowMobileMenu(false);
  };

  // Don't show on login/register pages
  if (location.pathname === '/login' || location.pathname === '/register') {
    return null;
  }

  return (
    <>
      <header id="account-header" className="account-header sticky-top bg-body mb-3">
        <div className="account-header__branding">
          {/* Top banner - Account focused */}
          <div className="w-100 py-2 bg-primary">
            <div className="container-fluid">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <p className="mb-0 text-white">
                    {currentCompany && (
                      <>
                        <i className="bi bi-building me-2"></i>
                        <strong>{currentCompany.displayName || currentCompany.name}</strong>
                      </>
                    )}
                    {user && (
                      <>
                        <span className="mx-2">•</span>
                        <i className="bi bi-person-circle me-1"></i>
                        <strong>{getUserDisplayName()}</strong>
                        {isEmp && (
                          <span className="badge bg-info ms-2">Employee Preview</span>
                        )}
                      </>
                    )}
                  </p>
                </div>
                <div className="col-md-6 text-md-end d-none d-md-block">
                  <div className="d-flex align-items-center justify-content-md-end gap-3">
                    {isEmp ? (
                      currentCompany && (
                        <div className="text-white">
                          <small>
                            <i className="bi bi-building me-1"></i>
                            Previewing: <strong>{currentCompany.displayName || currentCompany.companyName}</strong>
                          </small>
                        </div>
                      )
                    ) : (
                      currentStore && (
                        <div className="text-white">
                          <small>
                            <i className="bi bi-shop me-1"></i>
                            Current Store: <strong>{getStoreDisplayName()}</strong>
                          </small>
                        </div>
                      )
                    )}
                    
                    <button 
                      onClick={handleLogout}
                      className="btn btn-link text-white p-0 text-decoration-none"
                    >
                      <small>
                        <i className="bi bi-box-arrow-right me-1"></i>
                        Logout
                      </small>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main header section - Account Navigation */}
          <div className="account-header__main container-fluid bg-white">
            <div className="row py-3">
              {/* Mobile menu toggle */}
              <div className="col-4 d-lg-none">
                <nav className="navbar navbar-expand-lg py-0">
                  <button 
                    className={`navbar-toggler ${!showMobileMenu ? 'collapsed' : ''} border-0 shadow-none`}
                    type="button" 
                    onClick={toggleMobileMenu}
                    aria-expanded={showMobileMenu}
                    aria-label="Toggle navigation"
                  >
                    <span className="toggler-icon top-bar"></span>
                    <span className="toggler-icon middle-bar"></span>
                    <span className="toggler-icon bottom-bar"></span>
                  </button>
                </nav>
              </div>

              {/* Logo */}
              <div className="col-4 col-lg-3 col-xxl-2 d-flex align-items-center justify-content-center justify-content-lg-start">
                <div className="logo">
                  <Link to="/account" className="custom-logo-link">
                    <span className="fw-bold fs-2 text-primary">
                      {import.meta.env.VITE_APP_NAME || 'Wholesale Store'}
                    </span>
                  </Link>
                </div>
              </div>

              {/* Account Navigation */}
              <div className="col-4 col-lg-6 col-xxl-8 d-none d-lg-flex align-items-center justify-content-center">
                <nav className="account-nav">
                  <ul className="nav nav-pills">
                    {/* Shop: back to the store (customers with a store selected; replaces the old Go Shopping button) */}
                    {!isEmp && canProceedToShopping() && (
                      <li className="nav-item">
                        <Link to="/" className="nav-link">
                          <i className="bi bi-shop me-1"></i>
                          Shop
                        </Link>
                      </li>
                    )}
                    <li className="nav-item">
                      <Link 
                        to="/account" 
                        className={`nav-link ${location.pathname === '/account' ? 'active' : ''}`}
                      >
                        <i className="bi bi-speedometer2 me-1"></i>
                        Dashboard
                      </Link>
                    </li>
                    {!isEmp && (
                      <li className="nav-item">
                        <Link
                          to="/account/orders"
                          className={`nav-link ${location.pathname.includes('/account/orders') ? 'active' : ''}`}
                        >
                          <i className="bi bi-box-seam me-1"></i>
                          Orders
                        </Link>
                      </li>
                    )}
                    {!isEmp && (
                      <li className="nav-item">
                        <Link
                          to="/account/back-orders"
                          className={`nav-link ${location.pathname.includes('/account/back-orders') ? 'active' : ''}`}
                        >
                          <i className="bi bi-clock-history me-1"></i>
                          Back Orders
                        </Link>
                      </li>
                    )}
                    {!isEmp && creditsEnabled && (
                      <li className="nav-item">
                        <Link
                          to="/account/credits"
                          className={`nav-link ${location.pathname.includes('/account/credits') ? 'active' : ''}`}
                        >
                          <i className="bi bi-wallet2 me-1"></i>
                          Credits
                        </Link>
                      </li>
                    )}
                    {!isEmp && (
                      <li className="nav-item">
                        <Link
                          to="/account/profile"
                          className={`nav-link ${location.pathname.includes('/account/profile') ? 'active' : ''}`}
                        >
                          <i className="bi bi-person-gear me-1"></i>
                          Profile
                        </Link>
                      </li>
                    )}
                    {!isEmp && (
                      <li className="nav-item">
                        <Link
                          to="/account/notifications"
                          className={`nav-link ${location.pathname.includes('/account/notifications') ? 'active' : ''}`}
                        >
                          <i className="bi bi-bell me-1"></i>
                          Notifications
                        </Link>
                      </li>
                    )}
                    <li className="nav-item d-none">
                      <Link 
                        to="/support" 
                        className={`nav-link ${location.pathname.includes('/support') ? 'active' : ''}`}
                      >
                        <i className="bi bi-question-circle me-1"></i>
                        Support
                      </Link>
                    </li>
                  </ul>
                </nav>
              </div>

              {/* Right side actions */}
              <div className="col-4 col-lg-3 col-xxl-2 d-flex align-items-center justify-content-end">
                <div className="account-actions d-flex align-items-center gap-2">
                  {/* Store/Company selection status */}
                  {isEmp ? (
                    canProceedToShopping() ? (
                      <Link
                        to="/"
                        className="btn btn-info btn-sm"
                        title="Preview products"
                      >
                        <i className="bi bi-eye me-1"></i>
                        <span className="d-none d-md-inline">Preview Products</span>
                      </Link>
                    ) : (
                      <div className="text-warning">
                        <small>
                          <i className="bi bi-exclamation-triangle me-1"></i>
                          <span className="d-none d-md-inline">Select Company</span>
                        </small>
                      </div>
                    )
                  ) : (
                    <>
                      {!currentStore ? (
                        <div className="text-warning">
                          <small>
                            <i className="bi bi-exclamation-triangle me-1"></i>
                            <span className="d-none d-md-inline">Select Store</span>
                          </small>
                        </div>
                      ) : null}

                      {/* Quick Order - only for customers with store selected */}
                      {canProceedToShopping() && (
                        <Link
                          to="/quick-order"
                          className="btn btn-outline-primary btn-sm"
                        >
                          <i className="bi bi-lightning me-1"></i>
                          <span className="d-none d-lg-inline">Quick Order</span>
                        </Link>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        <div className="d-lg-none">
          <div 
            className={`offcanvas offcanvas-start bg-body ${showMobileMenu ? 'show' : ''}`} 
            style={{ 
              visibility: showMobileMenu ? 'visible' : 'hidden',
              transform: showMobileMenu ? 'translateX(0)' : 'translateX(-100%)',
              transition: 'transform 0.3s ease-in-out'
            }}
          >
            <div className="offcanvas-header border-bottom">
              <h5 className="offcanvas-title">Account Menu</h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={closeMobileMenu}
                aria-label="Close"
              ></button>
            </div>
            
            <div className="offcanvas-body">
              <ul className="list-unstyled account-mobile-menu">
                {/* User info */}
                <li className="mb-3 pb-3 border-bottom">
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" 
                           style={{width: '40px', height: '40px'}}>
                        <i className="bi bi-person"></i>
                      </div>
                    </div>
                    <div>
                      <div className="fw-semibold">{getUserDisplayName()}</div>
                      {isEmp ? (
                        currentCompany ? (
                          <small className="text-info">
                            <i className="bi bi-building me-1"></i>
                            {currentCompany.displayName || currentCompany.companyName}
                            <span className="badge bg-info ms-1" style={{ fontSize: '0.6rem' }}>Preview</span>
                          </small>
                        ) : (
                          <small className="text-warning">
                            <i className="bi bi-exclamation-triangle me-1"></i>
                            No company selected
                          </small>
                        )
                      ) : currentStore ? (
                        <small className="text-success">
                          <i className="bi bi-check-circle me-1"></i>
                          {getStoreDisplayName()}
                        </small>
                      ) : (
                        <small className="text-warning">
                          <i className="bi bi-exclamation-triangle me-1"></i>
                          No store selected
                        </small>
                      )}
                    </div>
                  </div>
                </li>
                
                {!isEmp && canProceedToShopping() && (
                  <li className="mb-2">
                    <Link
                      to="/"
                      className="d-flex align-items-center text-decoration-none text-dark p-2 rounded"
                      onClick={closeMobileMenu}
                    >
                      <i className="bi bi-shop me-3"></i>
                      Shop
                    </Link>
                  </li>
                )}

                <li className="mb-2">
                  <Link 
                    to="/account" 
                    className="d-flex align-items-center text-decoration-none text-dark p-2 rounded"
                    onClick={closeMobileMenu}
                  >
                    <i className="bi bi-speedometer2 me-3"></i>
                    Dashboard
                  </Link>
                </li>
                
                {!isEmp && (
                  <li className="mb-2">
                    <Link
                      to="/account/orders"
                      className="d-flex align-items-center text-decoration-none text-dark p-2 rounded"
                      onClick={closeMobileMenu}
                    >
                      <i className="bi bi-box-seam me-3"></i>
                      Order History
                    </Link>
                  </li>
                )}

                {!isEmp && (
                  <li className="mb-2">
                    <Link
                      to="/account/back-orders"
                      className="d-flex align-items-center text-decoration-none text-dark p-2 rounded"
                      onClick={closeMobileMenu}
                    >
                      <i className="bi bi-clock-history me-3"></i>
                      Back Orders
                    </Link>
                  </li>
                )}

                {!isEmp && creditsEnabled && (
                  <li className="mb-2">
                    <Link
                      to="/account/credits"
                      className="d-flex align-items-center text-decoration-none text-dark p-2 rounded"
                      onClick={closeMobileMenu}
                    >
                      <i className="bi bi-wallet2 me-3"></i>
                      Credits
                    </Link>
                  </li>
                )}

                {!isEmp && (
                  <li className="mb-2">
                    <Link
                      to="/account/profile"
                      className="d-flex align-items-center text-decoration-none text-dark p-2 rounded"
                      onClick={closeMobileMenu}
                    >
                      <i className="bi bi-person-gear me-3"></i>
                      Profile Settings
                    </Link>
                  </li>
                )}

                {!isEmp && (
                  <li className="mb-2">
                    <Link
                      to="/account/notifications"
                      className="d-flex align-items-center text-decoration-none text-dark p-2 rounded"
                      onClick={closeMobileMenu}
                    >
                      <i className="bi bi-bell me-3"></i>
                      Notifications
                    </Link>
                  </li>
                )}
                
                <li className="mb-2">
                  <Link 
                    to="/support" 
                    className="d-flex align-items-center text-decoration-none text-dark p-2 rounded"
                    onClick={closeMobileMenu}
                  >
                    <i className="bi bi-question-circle me-3"></i>
                    Help & Support
                  </Link>
                </li>

                {canProceedToShopping() && (
                  <>
                    {isEmp && (
                      <li className="my-3 border-top pt-3">
                        <Link to="/" className="btn btn-info w-100" onClick={closeMobileMenu}>
                          <i className="bi bi-eye me-2"></i>
                          Preview Products
                        </Link>
                      </li>
                    )}

                    {!isEmp && (
                      <li className="my-3 border-top pt-3">
                        <Link
                          to="/quick-order"
                          className="btn btn-outline-primary w-100"
                          onClick={closeMobileMenu}
                        >
                          <i className="bi bi-lightning me-2"></i>
                          Quick Order
                        </Link>
                      </li>
                    )}
                  </>
                )}
                
                <li className="mt-4 pt-3 border-top">
                  <button 
                    onClick={handleLogout}
                    className="btn btn-outline-danger w-100"
                  >
                    <i className="bi bi-box-arrow-right me-2"></i>
                    Logout
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </header>

      {/* Custom CSS */}
      <style>{`
        .account-header {
          border-bottom: 1px solid #e9ecef;
        }

        .account-nav .nav-pills .nav-link {
          border-radius:0;
          margin: 0 0.25rem;
          color: #6c757d;
          font-weight: 500;
        }

        .account-nav .nav-pills .nav-link:hover {
          background-color: #f8f9fa;
          color: #55A62D;
        }

        .account-nav .nav-pills .nav-link.active {
          background-color: #55A62D;
          color: white;
        }

        .toggler-icon {
          display: block;
          width: 25px;
          height: 3px;
          margin: 3px 0;
          background-color: #333;
          transition: 0.3s;
        }
        
        .navbar-toggler.collapsed .toggler-icon.top-bar {
          transform: rotate(0deg);
        }
        
        .navbar-toggler.collapsed .toggler-icon.middle-bar {
          opacity: 1;
        }
        
        .navbar-toggler.collapsed .toggler-icon.bottom-bar {
          transform: rotate(0deg);
        }
        
        .navbar-toggler .toggler-icon.top-bar {
          transform: rotate(45deg) translate(6px, 6px);
        }
        
        .navbar-toggler .toggler-icon.middle-bar {
          opacity: 0;
        }
        
        .navbar-toggler .toggler-icon.bottom-bar {
          transform: rotate(-45deg) translate(6px, -6px);
        }

        .offcanvas {
          max-width: 320px;
        }

        .account-mobile-menu .nav-link:hover,
        .account-mobile-menu a:hover {
          background-color: #f8f9fa;
        }
      `}</style>
    </>
  );
};

export default AccountHeader;